import { Component, Input, OnInit, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { PositionService } from '../services/position.service';

const CAREER_LEVEL_MAP: Record<string, string> = {
  'Entry level':       'ENTRY_LEVEL',
  'Experienced':       'EXPERIENCED',
  'Manager':           'MANAGER',
  'Senior management': 'SENIOR_MANAGEMENT',
  'C-Level':           'C_LEVEL',
  'Student':           'STUDENT',
};

@Component({
  selector: 'app-position-form',
  templateUrl: './position-form.component.html',
  styleUrls: ['./position-form.component.css']
})
export class PositionFormComponent implements OnInit {

  @Input() positionId?: string | number;
  @Input() initialValues?: { jobPosition: string; department: string; careerLevel: string };

  form!: FormGroup;
  isLoading = false;

  openDropdowns: { [key: string]: boolean } = {};

  toggleDropdown(dropdownName: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    for (const key in this.openDropdowns) {
      if (key !== dropdownName) {
        this.openDropdowns[key] = false;
      }
    }
    this.openDropdowns[dropdownName] = !this.openDropdowns[dropdownName];
  }

  selectDropdownOption(controlName: string, value: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.form.contains(controlName)) {
      this.form.get(controlName)?.setValue(value);
      this.form.get(controlName)?.markAsTouched();
    }
    this.openDropdowns[controlName] = false;
  }

  @HostListener('document:click', ['$event'])
  closeAllDropdowns(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-select')) {
      for (const key in this.openDropdowns) {
        this.openDropdowns[key] = false;
      }
    }
  }

  getCareerLevelLabel(): string {
    const val = this.form.get('careerLevel')?.value;
    const opt = this.data.careerLevels.find(cl => cl.label === val);
    if (!opt) return this.translate.instant('Choose level');
    const label = this.translate.instant(opt.label);
    const sub = opt.sub ? ` (${this.translate.instant(opt.sub)})` : '';
    return `${label}${sub}`;
  }

  get isEditMode(): boolean { return !!this.positionId; }

  get userLang(): string {
    return this.translate.currentLang || 'en';
  }

  goBack(): void {
    window.history.back();
  }

  data = {
    departments: [
      'Engineering', 'Design & Creative', 'Marketing', 'Sales',
      'Customer Support', 'Human Resources', 'Finance & Accounting',
      'Product', 'Operations', 'Legal'
    ],
    careerLevels: [
      { label: 'Entry level',       sub: 'Junior/Fresh grad' },
      { label: 'Experienced',       sub: 'non-manager' },
      { label: 'Manager',           sub: '' },
      { label: 'Senior management', sub: '' },
      { label: 'C-Level',           sub: 'CEO, General, Board' },
      { label: 'Student',           sub: 'undergrad/postgrad' },
    ]
  };

private readonly swalConfig = {
  customClass: {
    popup:         'swal-popup',
    title:         'swal-title',
    htmlContainer: 'swal-message',
    confirmButton: 'swal-confirm-btn',
    cancelButton:  'swal-cancel-btn',
  },
  buttonsStyling: false,
  reverseButtons: true,
  iconColor: '#D0385E',
  background: '#ffffff',
  color: '#1a1d23',
  didOpen: () => {
    const icon = document.querySelector('.swal2-icon.swal2-success') as HTMLElement;
    if (icon) icon.style.borderColor = '#F98650';

    const ring = document.querySelector('.swal2-success-ring') as HTMLElement;
    if (ring) ring.style.borderColor = 'rgba(249,134,80,0.25)';

    document.querySelectorAll<HTMLElement>('[class^="swal2-success-line"]').forEach(el => {
      el.style.backgroundColor = '#D0385E';
    });
  }
};
  constructor(
    private fb: FormBuilder,
    private positionService: PositionService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    const idFromRoute = this.route.snapshot.paramMap.get('id');
    if (idFromRoute) {
      this.positionId = idFromRoute;
      this.loadPosition(idFromRoute);
    }
  }
  private loadPosition(id: string): void {
    this.isLoading = true;
    this.positionService
      .getPositionById(id)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res) => {
          const careerLevelLabel =
            Object.entries(CAREER_LEVEL_MAP).find(([, v]) => v === res.jobLevel)?.[0]
            ?? res.jobLevel;

          this.form.patchValue({
            jobPosition: res.title,
            department:  res.department,
            careerLevel: careerLevelLabel,
          });
        },
        error: () => {
          Swal.fire({
            ...this.swalConfig,
            icon:             'error',
            title:            this.translate.instant('Failed to load'),
            text:             this.translate.instant('Could not load position data. Please try again.'),
            confirmButtonText: this.translate.instant('OK'),
          });
        }
      });
  }

  private buildForm(): void {
    this.form = this.fb.group({
      jobPosition: [this.initialValues?.jobPosition ?? '', Validators.required],
      department:  [this.initialValues?.department  ?? '', Validators.required],
      careerLevel: [this.initialValues?.careerLevel ?? '', Validators.required],
    });
  }

  private buildPayload() {
    const { jobPosition, department, careerLevel } = this.form.value;
    return {
      title:      jobPosition,
      department,
      jobLevel:   CAREER_LEVEL_MAP[careerLevel] ?? careerLevel.toUpperCase().replace(/ /g, '_'),
    };
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isEditMode ? this.handleUpdate() : this.handleCreate();
  }
private handleCreate(): void {
  this.isLoading = true;
  this.positionService
    .createPosition(this.buildPayload())
    .pipe(finalize(() => (this.isLoading = false)))
    .subscribe({
      next: () => {
        Swal.fire({
          ...this.swalConfig,
          icon:             'success',
          title:            this.translate.instant('Position Created!'),
          text:             this.translate.instant('The new job position has been added successfully.'),
          confirmButtonText: this.translate.instant('Great!'),
        }).then(() => {
       
          this.router.navigate(['/company-admin/positions']);
        });
        this.form.reset();
      },
      error: (err) => {
        Swal.fire({
          ...this.swalConfig,
          icon:             'error',
          title:            this.translate.instant('Something went wrong'),
          text:             err?.error?.message ?? this.translate.instant('Failed to create position. Please try again.'),
          confirmButtonText: this.translate.instant('Try Again'),
        });
      }
    });
}

 private async handleUpdate(): Promise<void> {
  const result = await Swal.fire({
    ...this.swalConfig,
    icon:              'question',
    title:             this.translate.instant('Save Changes?'),
    text:              this.translate.instant('Are you sure you want to update this job position?'),
    showCancelButton:  true,
    confirmButtonText: this.translate.instant('Yes, Save'),
    cancelButtonText:  this.translate.instant('Cancel'),
  });

  if (!result.isConfirmed) return;

  this.isLoading = true;
  this.positionService
    .updatePosition(this.positionId!, this.buildPayload())
    .pipe(finalize(() => (this.isLoading = false)))
    .subscribe({
      next: () => {
        Swal.fire({
          ...this.swalConfig,
          icon:             'success',
          title:            this.translate.instant('Position Updated!'),
          text:             this.translate.instant('The job position has been updated successfully.'),
          confirmButtonText: this.translate.instant('Great!'),
        }).then(() => {
          this.router.navigate(['/company-admin/positions']);
        });
      },
      error: (err) => {
        Swal.fire({
          ...this.swalConfig,
          icon:             'error',
          title:            this.translate.instant('Update Failed'),
          text:             err?.error?.message ?? this.translate.instant('Failed to update position. Please try again.'),
          confirmButtonText: this.translate.instant('Try Again'),
        });
      }
    });
}
}